import {BriefStatus} from "@buildwithflux/models";
import {createSelector} from "reselect";

import {ICopilotChatStore} from "../useCopilotChatStore";

import {createThreadMessagesSelector} from "./createThreadMessagesSelector";

/**
 * Brief statuses that indicate the autopilot is no longer actively working.
 * When the brief is in one of these terminal states, we should not consider
 * the thread as streaming, even if there are messages with stale streaming statuses.
 */
const TERMINAL_BRIEF_STATUSES: BriefStatus[] = ["completed", "failed", "stopped", "ambiguous", "impossible", "discard"];

/**
 * Maximum age in milliseconds for a message to be considered actively streaming.
 * If a message has been in init/streaming/thinking status for longer than this,
 * it's considered stale (likely due to a backend crash/timeout) and we should
 * not block the user from sending new messages.
 * 10 minutes = 600000ms
 */
const STREAMING_MESSAGE_STALENESS_THRESHOLD_MS = 10 * 60 * 1000;

// Selector to check if the thread is streaming, reusing the memoized thread messages
export const createIsThreadStreamingSelector = (threadUid: string) => {
    const threadMessagesSelector = createThreadMessagesSelector(threadUid);

    return createSelector(
        [threadMessagesSelector, (state: ICopilotChatStore) => state.threads[threadUid]?.latest_brief?.brief_status],
        (messages, briefStatus): boolean => {
            // If the brief is in a terminal state, the autopilot is no longer working
            // so we should not consider the thread as streaming
            if (briefStatus && TERMINAL_BRIEF_STATUSES.includes(briefStatus)) {
                return false;
            }

            if (!messages || messages.length === 0) {
                return false;
            }

            // Find the latest copilot or execution_step message by updated_at timestamp
            // We only check the latest message because if it's completed, we shouldn't
            // block the user from sending new messages due to older stale messages
            let latestRelevantMessage = null;
            let latestTimestamp = 0;

            for (const message of messages) {
                const metaData = message.meta_data;
                if (metaData?.type !== "copilot" && metaData?.type !== "execution_step") {
                    continue;
                }
                const timestamp = message.updated_at ?? message.created_at;
                if (timestamp > latestTimestamp) {
                    latestTimestamp = timestamp;
                    latestRelevantMessage = message;
                }
            }

            if (!latestRelevantMessage) {
                return false;
            }

            const metaData = latestRelevantMessage.meta_data;
            if (metaData?.type !== "copilot" && metaData?.type !== "execution_step") {
                return false;
            }

            const isStreaming =
                metaData.message_status === "streaming" ||
                metaData.message_status === "init" ||
                metaData.message_status === "thinking";

            // cancel_streaming_at_position only exists on copilot messages
            const isCancelled = metaData.type === "copilot" && metaData.cancel_streaming_at_position !== undefined;

            // Check if the message is stale (stuck in streaming state for too long)
            // This handles cases where the backend crashed/timed out without updating the message status
            const now = Date.now();
            const messageAge = now - (latestRelevantMessage.updated_at ?? latestRelevantMessage.created_at);
            const isStale = messageAge > STREAMING_MESSAGE_STALENESS_THRESHOLD_MS;

            return isStreaming && !isCancelled && !isStale;
        },
    );
};
